'use client';
import useProducts from '@hooks/product/useProducts';
import { useEffect, useMemo } from 'react';
import { RightArrow, LeftArrow } from '@repo/ui/src/components/Carousels/CarouselArrows';
import { logProduct } from '@utils/productWrapper';
import Product from '@models/product';
import { useIsSM, useIsMD, useIsLG, useIsXL } from '@repo/ui/src/utils/useMediaQuery';

import { CarouselProvider, Slider, ButtonBack, ButtonNext } from 'pure-react-carousel';
import AnimatedSlider from './_AnimatedSlider';
import CarouselItem from './_CarouselItem';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { LinkProps } from '@repo/ui/src/types/link';
import { MetaProps } from '@repo/ui/src/types/meta';
import { ProductModelProps } from '@typings/product';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import SlideWrapper from './_SlideWrapper';

export interface GeneralCarouselProps {
  title?: string;
  description?: string;
  viewAllLink?: LinkProps;
  headerLeft?: boolean;
  maxVisibleCards?: number;
  darkMode?: boolean;
  linkAsButton?: boolean;
  _testID?: string;
  _meta?: MetaProps;
  body?: Array<any>;
  _data?: Array<any>;
  affirmText?: string;
  includeAffirm?: boolean;
  whiteBackground?: boolean;
  saleText?: string;
  newText?: string;
  outOfStockText?: string;
  smallCards?: boolean;
  noPadding?: boolean;
  schema?: string;
  className?: string;
}

const GeneralCarousel = ({
  title,
  description,
  viewAllLink,
  body,
  _data = body,
  _testID,
  _meta,
  headerLeft,
  maxVisibleCards = 3.5,
  darkMode,
  linkAsButton,
  affirmText,
  includeAffirm,
  whiteBackground,
  saleText,
  newText,
  outOfStockText,
  noPadding,
  smallCards = false,
  schema,
  className,
}: GeneralCarouselProps) => {
  const { products } = useProducts(
    _data?.filter(
      (item) => item?._meta?.schema === 'https://traegergrills.com/data/contentTypes/product.json',
    ),
  );
  const items = products?.length ? products : _data;
  const isSmall = useIsSM();
  const isMedium = useIsMD();
  const isLarge = useIsLG();
  const isXL = useIsXL();
  const { locale } = useCurrentUrl();

  useEffect(() => {
    if (items?.[0]?.schema?.includes('product')) {
      const _products = items.map((p: ProductModelProps, i: number) => new Product(p, i));
      logProduct('PRODUCT_LIST_VIEWED', {
        product: _products ?? [],
        locale,
        list_name: title,
        list_id: _meta?.deliveryId,
      });
    }
  }, []);

  const slideCount = useMemo(() => {
    if (!items?.length) {
      return 1;
    }
    if (items?.length === 4 && isXL) {
      return Math.min(4, maxVisibleCards);
    }
    if (items?.length <= 3 && isLarge) {
      return Math.min(items.length, maxVisibleCards);
    }
    if (items?.length <= 2 && isMedium) {
      return Math.min(items.length, maxVisibleCards);
    }
    if (!isSmall) {
      return Math.min(1, maxVisibleCards);
    }
    if (!isMedium) {
      return Math.min(1.5, maxVisibleCards);
    }
    if (!isLarge) {
      return Math.min(headerLeft ? 1.5 : 2.5, maxVisibleCards);
    }
    if (!isXL) {
      return Math.min(headerLeft ? 2.5 : 3.5, maxVisibleCards);
    }
    return maxVisibleCards;    
  }, [isSmall, isMedium, isLarge, isXL, headerLeft, items?.length]);

  const hideSlider = items?.length === slideCount;

  const center = hideSlider && items.length < 5;

  if (!items) return null;

  return (
    <div
      className={classNames(
        darkMode && 'tw-bg-neutral-600',
        whiteBackground && 'tw-bg-white',
        className,
        !darkMode && !whiteBackground && 'tw-bg-warm-200',
      )}
    >
      <div
        role="region"
        aria-label={`${title} carousel`}
        data-test-id={`reference_carousel_${_testID}`}
        className={classNames(
          'tw-py-8 md:tw-py-20 tw-max-w-[1680px] tw-m-auto',
          headerLeft && 'tw-flex tw-flex-col md:tw-flex-row',
          hideSlider && 'tw-pr-10 xl:tw-pr-0',
          !noPadding && 'tw-pl-4 sm:tw-pl-9 md:tw-pl-20',
        )}
      >
        <p className="sr-only">This is a carousel. Tab through the slides for content.</p>
        {title && (
          <div
            className={classNames(
              'tw-flex tw-justify-between tw-pb-8 tw-gap-x-8 tw-pr-5 sm:tw-pb-10 tw-flex-col sm:tw-flex-row md:tw-pr-10',
              headerLeft &&
                'tw-flex sm:tw-flex-col sm:tw-pb-8 tw-justify-start sm:tw-justify-start md:tw-justify-start md:tw-w-1/4 md:tw-pb-0',
            )}
          >
            {title && (
              <div>
                <h2
                  className={classNames(
                    'tra-text-web-heading-2 tw-mb-6',
                    darkMode && 'tw-text-white',
                  )}
                >
                  {title}
                </h2>
                {includeAffirm && (
                  <p className={classNames('tw-pr-10 tw-mb-0', darkMode && 'tw-text-white')}>
                    {affirmText}
                  </p>
                )}
                {description && (
                  <p className={classNames('tw-pr-10 tw-mb-0', darkMode && 'tw-text-white')}>
                    {description}
                  </p>
                )}
                <hr
                  className={classNames(
                    'tw-border-2 tw-w-10 tw-border-brand-300 tw-my-3 sm:tw-mt-6 sm:tw-mb-0',
                    headerLeft && 'sm:tw-mt-4 sm:tw-mb-4',
                  )}
                />
              </div>
            )}
            <div
              className={classNames(
                'md:tw-w-6/12 tw-flex ',
                headerLeft ? 'md:tw-w-full md:tw-pr-0' : 'md:tw-pr-20 sm:tw-justify-end',
              )}
            >
              {items && items?.length > 2 && viewAllLink?.link && (
                <div className={classNames(!description && 'tw-pt-10 md:tw-pt-2')}>
                  <Button
                    variant={linkAsButton ? ButtonVariant.primary : ButtonVariant.knife}
                    as={Anchor}
                    {...viewAllLink}
                    className={classNames('tw-h-auto', darkMode && 'tw-text-white')}
                  >
                    {viewAllLink?.displayName}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <CarouselProvider
          naturalSlideWidth={100}
          naturalSlideHeight={140}
          totalSlides={items.length}
          visibleSlides={slideCount}
          className={classNames(headerLeft && 'md:tw-w-3/4 tw-flex tw-flex-col')}
          isIntrinsicHeight
          dragEnabled={false}
        >
          <Slider
            data-testid="gc-carousel"
            tabIndex={-1}
            role="group"
            className={classNames(center && 'tw-flex tw-items-center tw-justify-center')}
          >
            {items?.filter(Boolean).map((item: any, i: number) => (
              <SlideWrapper key={i} index={i} className="tw-pr-6" length={items?.length}>
                <CarouselItem
                  item={item}
                  isCarousel
                  saleText={saleText}
                  outOfStockText={outOfStockText}
                  newText={newText}
                  small={smallCards}
                  darkMode={darkMode}
                  schema={schema}
                />
              </SlideWrapper>
            ))}
          </Slider>
          {!hideSlider && (
            <div
              className="tw-flex tw-pr-5 md:tw-pr-20 tw-pt-10 tw-w-full tw-justify-between tw-items-center"
              data-testid="gc-slider"
            >
              <div className="tw-w-full tw-mr-10">
                <AnimatedSlider darkMode={darkMode} />
              </div>
              <div className="tw-flex tw-w-28 tw-justify-between tw-gap-x-2">
                <ButtonBack aria-label="previous">
                  <LeftArrow aria-label={null} />
                </ButtonBack>
                <ButtonNext aria-label="next">
                  <RightArrow aria-label={null} />
                </ButtonNext>
              </div>
            </div>
          )}
        </CarouselProvider>
      </div>
    </div>
  );
};

export default GeneralCarousel;
